/**
 * Privacy Service (GDPR / CCPA)
 *
 * Client-side service for:
 * - Recording and reading privacy consent (collection: privacyConsents)
 * - Exporting the user's own data (GDPR "right to access")
 * - Deleting the user's own data (GDPR "right to be forgotten")
 *
 * All operations act on the authenticated user's own data, which the Firestore
 * security rules already permit (own user doc, subcollections, own consent,
 * own audit log). No cross-user access occurs here.
 *
 * @see WI-012c Phone Auth Production Security
 */

import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { FirestoreService } from './firestore.service';
import { devLog, devError } from '@core';
import { User } from '@shared/models/user.models';
import {
  ConsentPurposes,
  PrivacyConsent,
  UserDataExport,
  CheckInExport,
  ContactExport,
  AuditEventExport,
} from '@core/models/privacy.types';
import { AuditEvent } from '@core/models/security.types';

/**
 * Current privacy consent policy version. Bump when the policy materially
 * changes so consent can be re-collected.
 */
export const CURRENT_CONSENT_VERSION = '1.0';

/**
 * A value that may be a Firestore Timestamp, a plain {seconds,nanoseconds}
 * object, or absent. Used when normalizing timestamps for export.
 */
type TimestampLike =
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | null
  | undefined;

/**
 * Minimal shape of a stored check-in needed for export. Defined locally to
 * avoid a core -> feature dependency.
 */
interface RawCheckIn {
  triggerTime?: TimestampLike;
  status?: string;
  note?: string;
  createdAt?: TimestampLike;
  resolvedAt?: TimestampLike;
}

/**
 * Minimal shape of a stored contact needed for export.
 */
interface RawContact {
  name?: string;
  relationship?: string;
  verified?: boolean;
  createdAt?: TimestampLike;
}

@Injectable({
  providedIn: 'root',
})
export class PrivacyService {
  private firestore = inject(FirestoreService);
  private functions = inject(Functions);

  /**
   * Record the user's privacy consent.
   *
   * `necessary` is always forced to true (the service cannot operate without
   * it), matching the Firestore rule for privacyConsents.
   *
   * @param userId - Authenticated user id
   * @param purposes - Consent choices per purpose
   * @param version - Consent policy version (defaults to current)
   */
  async recordConsent(
    userId: string,
    purposes: ConsentPurposes,
    version: string = CURRENT_CONSENT_VERSION
  ): Promise<void> {
    const consent = {
      userId,
      consentVersion: version,
      purposes: { ...purposes, necessary: true },
      consentedAt: this.firestore.fieldValue.serverTimestamp(),
    };

    await this.firestore.setDoc(`privacyConsents/${userId}`, consent);
    devLog('[PrivacyService] Consent recorded', { userId, version });
  }

  /**
   * Read the user's stored privacy consent.
   *
   * @param userId - Authenticated user id
   * @returns The consent record, or null if none exists
   */
  async getConsent(userId: string): Promise<PrivacyConsent | null> {
    return this.firestore.getDoc<PrivacyConsent>(`privacyConsents/${userId}`);
  }

  /**
   * Export all of the user's personal data (GDPR right to access).
   *
   * @param userId - Authenticated user id
   * @returns A structured export of profile, check-ins, contacts, and audit log
   */
  async exportUserData(userId: string): Promise<UserDataExport> {
    devLog('[PrivacyService] Export requested', { userId });

    const [profile, rawCheckIns, rawContacts, auditEvents] = await Promise.all([
      this.firestore.getDoc<User>(`users/${userId}`),
      this.firestore.getCollection<RawCheckIn>(`users/${userId}/checkIns`),
      this.firestore.getCollection<RawContact>(`users/${userId}/contacts`),
      this.firestore.getCollection<AuditEvent>('auditLog', [
        { type: 'where', field: 'userId', operator: '==', value: userId },
      ]),
    ]);

    const checkIns: CheckInExport[] = rawCheckIns.map((checkIn) => ({
      triggerTime: this.toIso(checkIn.triggerTime),
      status: checkIn.status ?? 'unknown',
      note: checkIn.note,
      createdAt: this.toIso(checkIn.createdAt),
      resolvedAt: checkIn.resolvedAt ? this.toIso(checkIn.resolvedAt) : undefined,
    }));

    const contacts: ContactExport[] = rawContacts.map((contact) => ({
      name: contact.name ?? '',
      relationship: contact.relationship ?? '',
      verified: contact.verified ?? false,
      createdAt: this.toIso(contact.createdAt),
    }));

    const auditLog: AuditEventExport[] = auditEvents.map((event) => ({
      type: event.type,
      timestamp: this.toIso(event.timestamp as unknown as TimestampLike),
      details: JSON.stringify(event.details ?? {}),
    }));

    return {
      exportedAt: new Date(),
      userId,
      profile,
      checkIns,
      contacts,
      auditLog,
    };
  }

  /**
   * Delete all of the user's data (GDPR right to be forgotten).
   *
   * Delegates to the deleteUserData Cloud Function, which cascades with
   * admin privileges across everything the client cannot delete itself
   * (notifications, device fingerprints, lockout state, rate limits) and
   * removes the Firebase Auth account last. The caller only needs to sign
   * out locally afterwards - the account no longer exists server-side.
   *
   * @param userId - Authenticated user id (log correlation only; the
   *                 function derives the target uid from the auth token)
   */
  async deleteUserData(userId: string): Promise<void> {
    devLog('[PrivacyService] Deletion requested', { userId });

    try {
      const result = await this.callDeleteUserData();
      devLog('[PrivacyService] User data deleted', {
        userId,
        deletedCounts: result.deletedCounts,
      });
    } catch (error) {
      devError('[PrivacyService] Failed to delete user data', error);
      throw error;
    }
  }

  /**
   * Invoke the deleteUserData Cloud Function. Separated for testability
   * (unit tests spy on this; the callable itself is exercised against the
   * emulator in integration testing).
   */
  private async callDeleteUserData(): Promise<{
    success: boolean;
    deletedCounts: Record<string, number>;
  }> {
    const deleteUserDataFn = httpsCallable<
      Record<string, never>,
      { success: boolean; deletedCounts: Record<string, number> }
    >(this.functions, 'deleteUserData');
    const result = await deleteUserDataFn({});
    return result.data;
  }

  /**
   * Normalize a Firestore-style timestamp to an ISO 8601 string.
   *
   * @param value - Timestamp, {seconds} object, or nullish
   * @returns ISO date string (empty string when the value is absent)
   */
  private toIso(value: TimestampLike): string {
    if (!value) {
      return '';
    }
    if (typeof (value as { toDate?: unknown }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate().toISOString();
    }
    const seconds = (value as { seconds?: number }).seconds;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000).toISOString();
    }
    return '';
  }
}
